import FiltersBar from "@/apps/clinic/pages/Referrals/FiltersBar";
import { api } from "@/apps/common/api-client";
import PageTitle from "@/apps/common/components/PageTitle";
import Table from "@/apps/common/components/Table";
import { APPOINTMENT_STATUS } from "@/apps/common/constants";
import { getFullName } from "@/apps/common/helpers";
import { formatDate, formatDateOfBirth } from "@/apps/common/helpers/date";
import { useRequest } from "ahooks";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Link, Navigate } from "react-router-dom";

const columns = [
  // {
  //   id: "id",
  //   Header: "ID",
  //   accessor: "id",
  //   sort: false
  // },
  {
    Header: "Patient name",
    accessor: "patientName",
    sort: true
  },
  {
    Header: "Patient DOB",
    accessor: "memberService.user.dateOfBirth",
    sort: false,
    Cell: ({ value = "111969" }: { value: string }) => (
      <span>{formatDateOfBirth(value)}</span>
    )
  },
  {
    Header: "Language",
    //accessor: "memberService.user.spokenLanguage",
    accessor: "language",
    sort: false
  },
  {
    Header: "Service type",
    accessor: "memberService.serviceType.name",
    sort: false
  },
  {
    Header: "Appointment time",
    accessor: "timeSlot.startDate",
    sort: false,
    Cell: ({ value }: { value: string }) => <span>{formatDate(value)}</span>
  },
  {
    Header: "Status",
    accessor: "status",
    sort: false,
    Cell: ({ value }: { value: string }) => (
      <span className={`services-status appointment-${value}`}>{value}</span>
    )
  },
  {
    id: "action",
    Header: "Actions",
    accessor: "id",
    Cell: ({ value }: { value: string }) => (
      <Link to={`/appointments/${value}`}>
        <Button size="sm" variant="btn btn-primary rounded-pill width-sm">
          View
        </Button>
      </Link>
    )
  }
];

export default function AppointmentsList() {
  const [filters, setFilters] = useState<any>({
    status: [
      APPOINTMENT_STATUS.PENDING,
      APPOINTMENT_STATUS.CONFIRMED,
      APPOINTMENT_STATUS.DUE
    ]
  });
  const { query, ...remainingFilters } = filters || {};
  const { data: appointments = [] } = useRequest(
    () =>
      api.appointments.findMany({
        query,
        filters: remainingFilters
      }),
    {
      refreshDeps: [filters],
      debounceWait: 300
    }
  );

  const appointmentsWithPatientName = useMemo(() => {
    const now = DateTime.now().minus({ hour: 2 }).toJSDate().getTime();

    const pastAppointmentCutoffIndex = appointments.findIndex((appointment) => {
      const time = new Date(appointment.timeSlot.startDate).getTime();
      return time < now;
    });

    const appointmentsWithPatientName = appointments.map((appointment) => {
      const languageLabel =
        appointment.memberService?.user?.spokenLanguage === "english"
          ? "English"
          : appointment.memberService?.user?.spokenLanguage === "en"
            ? "English"
            : appointment.memberService?.user?.spokenLanguage === "es"
              ? "Spanish"
              : "Unknown";

      ////console.debug (languageLabel)
      return {
        ...appointment,
        patientName: getFullName(appointment.memberService?.user),
        // language: appointment.memberService?.user?.spokenLanguage
        language: languageLabel
      };
    });

    const pastAppointments =
      pastAppointmentCutoffIndex >= 0
        ? appointmentsWithPatientName.slice(pastAppointmentCutoffIndex)
        : [];
    const futureAppointments =
      pastAppointmentCutoffIndex >= 0
        ? appointmentsWithPatientName.slice(0, pastAppointmentCutoffIndex)
        : appointmentsWithPatientName;

    const sortedFuturePendingAppointments = futureAppointments.toSorted(
      (a, b) => {
        const aTime = new Date(b.timeSlot.startDate).getTime();
        const bTime = new Date(a.timeSlot.startDate).getTime();

        return bTime - aTime;
      }
    );

    const pendingAppointmentStatus = [
      APPOINTMENT_STATUS.PENDING,
      APPOINTMENT_STATUS.DUE
    ];
    const futurePendingAppointments = sortedFuturePendingAppointments.filter(
      (appointment) => pendingAppointmentStatus.includes(appointment.status)
    );
    const futureOtherAppointments = sortedFuturePendingAppointments.filter(
      (appointment) => !pendingAppointmentStatus.includes(appointment.status)
    );

    return futurePendingAppointments.concat(
      futureOtherAppointments,
      pastAppointments
    );
  }, [appointments]);

  function providerPaymentUrl(cleanStorage: boolean = false): string {
    let returnVal: string = "";
    if (sessionStorage.getItem("provider_prepayment_url") != null) {
      returnVal = sessionStorage.getItem("provider_prepayment_url")!;
      if (cleanStorage) {
        sessionStorage.removeItem("provider_prepayment_url");
      }
    }

    return returnVal;
  }

  return (
    <>
      {sessionStorage.getItem("provider_prepayment_url") != null && (
        <Navigate
          to={{
            pathname: providerPaymentUrl(true)
          }}
        />
      )}
      <PageTitle
        breadCrumbItems={[{ label: "Appointments", path: "/" }]}
        title={"Member appointments"}
      />

      <Row>
        <Col>
          <FiltersBar values={filters} onChange={setFilters} />
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Table
            columns={columns}
            data={appointmentsWithPatientName}
            pageSize={10}
            isSortable={true}
            pagination={true}
            isSearchable={true}
          />
        </Card.Body>
      </Card>
    </>
  );
}
